import * as React from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    ButtonGroup,
    Card,
    CardActions,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    ListItemText,
    MenuItem,
    Menu,
    OutlinedInput,
    Popover,
    Select,
    SelectChangeEvent,
    Snackbar,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import CollectionsIcon from '@mui/icons-material/Collections';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useNavigate, useParams } from 'react-router-dom';
import {
    createDailyQuestImagePreset,
    createDailyQuestTemplate,
    deleteDailyQuestImagePreset,
    DestinationFolder,
    generateDailyQuestLocalizations,
    generateDailyQuestTemplateLocalizations,
    getDailyQuestImagePresets,
    getDailyQuestTemplate,
    getFunnelOptions,
    getUserTags,
    getUserTaskTemplates,
    resyncDailyQuestTemplateUserTaskTemplates,
    resolveStrapiMediaUrlForDisplay,
    updateDailyQuestTemplate,
    uploadFile,
} from '../Api';
import DailyQuestTemplateDto, {
    DailyQuestImageKind,
    DailyQuestImagePresetDto,
    DailyQuestTaskPoolEntryDto,
    UpsertDailyQuestTemplateDto,
    UserTaskTemplateDto,
    UserTaskType,
} from '../models/dtos/DailyQuestTemplateDto';
import UserTagDefinitionDto from '../models/dtos/UserTagDefinitionDto';
import { getLanguageName } from '../utils/languages';
import {
    DAILY_QUEST_NAVIGATION_MORE,
    DAILY_QUEST_NAVIGATION_PRESETS,
} from '../constants/dailyQuestNavigationTargets';

const SUPPORTED_LANGUAGES = ['en', 'de', 'fr', 'it', 'ro', 'ru', 'es', 'pt', 'pl', 'tr'];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const EMPTY_EN_LOCALIZATION = { en: '' };
type LocalizationGenerationMode = 'generate' | 'improve';

const emptyTemplateForm = (): UpsertDailyQuestTemplateDto => ({
    name: '',
    coverImageUrl: null,
    titleLocalizations: EMPTY_EN_LOCALIZATION,
    descriptionLocalizations: EMPTY_EN_LOCALIZATION,
    activationEvents: ['SessionStarted'],
    requiredTags: [],
    rewardBetcoins: 0,
    tasksToAssignCount: 1,
    taskPool: [],
    active: true,
});

function toForm(template: DailyQuestTemplateDto): UpsertDailyQuestTemplateDto {
    return {
        name: template.name,
        coverImageUrl: template.coverImageUrl ?? null,
        titleLocalizations: template.titleLocalizations,
        descriptionLocalizations: template.descriptionLocalizations,
        activationEvents: template.activationEvents,
        requiredTags: template.requiredTags,
        rewardBetcoins: template.rewardBetcoins,
        tasksToAssignCount: template.tasksToAssignCount,
        taskPool: template.taskPool,
        active: template.active,
    };
}

function blankTask(taskType: UserTaskType = 'START_SESSION'): DailyQuestTaskPoolEntryDto {
    return {
        taskType,
        userTaskTemplateImplemented: false,
        progressTargets: [1],
        iconUrl: null,
        nameLocalizations: { en: '' },
        descriptionLocalizations: { en: '' },
        navigationTarget: null,
    };
}

function userTaskTemplateReferenceName(template?: UserTaskTemplateDto): string {
    return template?.name || template?.code || template?.taskType || '';
}

function TaskImplementationDot({ implemented }: { implemented: boolean }) {
    return (
        <Box
            component="span"
            title={implemented ? 'Implemented in v7' : 'Not implemented in v7'}
            sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: implemented ? 'success.main' : 'grey.400',
                flex: '0 0 auto',
            }}
        />
    );
}

function taskPoolEntryDisplayName(task: DailyQuestTaskPoolEntryDto): string {
    return task.nameLocalizations?.en?.trim() || task.taskType;
}

function taskIconDisplayUrl(task: DailyQuestTaskPoolEntryDto): string | null {
    return task.iconUrl ? resolveStrapiMediaUrlForDisplay(task.iconUrl) ?? task.iconUrl : null;
}

function ProgressTargetsEditor({
    value,
    onChange,
    requiredError,
}: {
    value: number[];
    onChange: (value: number[]) => void;
    requiredError: boolean;
}) {
    const [draft, setDraft] = React.useState('');
    const [inputError, setInputError] = React.useState<string | null>(null);

    const addTarget = () => {
        const normalized = draft.trim();
        if (!/^\d+$/.test(normalized)) {
            setInputError('Enter a non-negative integer.');
            return;
        }
        const target = Number(normalized);
        if (!Number.isSafeInteger(target) || target < 0) {
            setInputError('Enter a non-negative integer.');
            return;
        }
        onChange([...value, target]);
        setDraft('');
        setInputError(null);
    };

    return (
        <Stack gap={1}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
                <TextField
                    label="Progress targets"
                    value={draft}
                    onChange={event => {
                        setDraft(event.target.value);
                        setInputError(null);
                    }}
                    onKeyDown={event => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            addTarget();
                        }
                    }}
                    error={Boolean(inputError) || requiredError}
                    helperText={inputError ?? (requiredError
                        ? 'Provide at least one progress target.'
                        : 'Add targets in the order in which they should rotate.')}
                    fullWidth
                />
                <Button variant="outlined" startIcon={<AddIcon />} onClick={addTarget} sx={{ minWidth: 108, height: 56 }}>
                    Add
                </Button>
            </Stack>
            <Stack direction="row" gap={1} flexWrap="wrap">
                {value.map((target, index) => (
                    <Chip
                        key={`${index}-${target}`}
                        label={target}
                        onDelete={() => onChange(value.filter((_, itemIndex) => itemIndex !== index))}
                        color="primary"
                        variant="outlined"
                        sx={{ minWidth: 64 }}
                    />
                ))}
            </Stack>
        </Stack>
    );
}

function sortedLocalizationEntries(value: Record<string, string>): [string, string][] {
    const languageOrder = new Map(SUPPORTED_LANGUAGES.map((code, index) => [code, index]));
    return Object.entries(value ?? {})
        .filter(([language]) => language.trim() !== '')
        .map(([language, text]) => [language.trim().toLowerCase(), text ?? ''] as [string, string])
        .sort(([left], [right]) => {
            if (left === 'en') return -1;
            if (right === 'en') return 1;
            return (languageOrder.get(left) ?? Number.MAX_SAFE_INTEGER) - (languageOrder.get(right) ?? Number.MAX_SAFE_INTEGER)
                || left.localeCompare(right);
        });
}

function languageSelectOptions(currentLanguage: string): string[] {
    return SUPPORTED_LANGUAGES.includes(currentLanguage)
        ? SUPPORTED_LANGUAGES
        : [currentLanguage, ...SUPPORTED_LANGUAGES];
}

function languageLabel(language: string): string {
    return `${getLanguageName(language)} (${language})`;
}

function LocalizationEditor({
    title,
    textLabel,
    value,
    onChange,
    minRows = 1,
}: {
    title: string;
    textLabel: string;
    value: Record<string, string>;
    onChange: (value: Record<string, string>) => void;
    minRows?: number;
}) {
    const entries = sortedLocalizationEntries(value);
    const englishText = value?.en ?? '';
    const otherEntries = entries.filter(([language]) => language !== 'en');
    const [expanded, setExpanded] = React.useState(false);
    const existingLanguages = new Set(['en', ...otherEntries.map(([language]) => language)]);
    const firstAvailableLanguage = SUPPORTED_LANGUAGES.find(language => language !== 'en' && !existingLanguages.has(language));

    const addLanguage = () => {
        if (!firstAvailableLanguage) return;
        onChange({ ...(value ?? {}), [firstAvailableLanguage]: '' });
        setExpanded(true);
    };

    const updateLanguage = (previousLanguage: string, nextLanguage: string) => {
        const normalizedLanguage = nextLanguage.trim().toLowerCase();
        if (!normalizedLanguage || normalizedLanguage === previousLanguage || existingLanguages.has(normalizedLanguage)) return;
        const next = { ...(value ?? {}) };
        const currentText = next[previousLanguage] ?? '';
        delete next[previousLanguage];
        next[normalizedLanguage] = currentText;
        onChange(next);
    };

    const updateText = (language: string, text: string) => {
        onChange({ ...(value ?? {}), [language]: text });
    };

    const removeLanguage = (language: string) => {
        const next = { ...(value ?? {}) };
        delete next[language];
        onChange(next);
    };

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">{title}</Typography>
                <Tooltip title={firstAvailableLanguage ? 'Add translation' : 'All supported languages are already added'}>
                    <span>
                        <IconButton
                            color="primary"
                            size="small"
                            onClick={addLanguage}
                            disabled={!firstAvailableLanguage}
                            aria-label="Add translation"
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>
            <Stack gap={1}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    gap={1}
                    alignItems={{ xs: 'stretch', md: 'flex-start' }}
                >
                    <TextField
                        label="Language"
                        select
                        value="en"
                        size="small"
                        disabled
                        sx={{ minWidth: 190 }}
                    >
                        <MenuItem value="en">{languageLabel('en')}</MenuItem>
                    </TextField>
                    <TextField
                        label={textLabel}
                        value={englishText}
                        onChange={event => updateText('en', event.target.value)}
                        size="small"
                        multiline={minRows > 1}
                        minRows={minRows}
                        fullWidth
                    />
                </Stack>
                <Button
                    variant="text"
                    size="small"
                    onClick={() => setExpanded(prev => !prev)}
                    endIcon={
                        <ArrowDropDownIcon
                            sx={{
                                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: theme => theme.transitions.create('transform', { duration: theme.transitions.duration.shortest }),
                            }}
                        />
                    }
                    sx={{ alignSelf: 'flex-start', px: 0.5 }}
                >
                    {otherEntries.length === 1 ? '1 language provided' : `${otherEntries.length} languages provided`}
                </Button>
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Stack gap={1}>
                        {otherEntries.map(([language, text]) => (
                    <Stack
                        key={language}
                        direction={{ xs: 'column', md: 'row' }}
                        gap={1}
                        alignItems={{ xs: 'stretch', md: 'flex-start' }}
                    >
                        <TextField
                            label="Language"
                            select
                            value={language}
                            onChange={event => updateLanguage(language, event.target.value)}
                            size="small"
                            sx={{ minWidth: 190 }}
                        >
                            {languageSelectOptions(language).map(option => (
                                <MenuItem
                                    key={option}
                                    value={option}
                                    disabled={existingLanguages.has(option) && option !== language}
                                >
                                    {languageLabel(option)}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label={textLabel}
                            value={text}
                            onChange={event => updateText(language, event.target.value)}
                            size="small"
                            multiline={minRows > 1}
                            minRows={minRows}
                            fullWidth
                        />
                        <Tooltip title="Remove translation">
                            <IconButton
                                color="error"
                                size="small"
                                onClick={() => removeLanguage(language)}
                                aria-label="Remove translation"
                                sx={{ mt: { md: 0.5 } }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                        ))}
                    </Stack>
                </Collapse>
                {otherEntries.length === 0 && expanded && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No additional translations added.
                    </Typography>
                )}
            </Stack>
        </Box>
    );
}

function GenerateLocalizationsButton({
    loading,
    loadingLabel,
    onGenerate,
}: {
    loading: boolean;
    loadingLabel: string;
    onGenerate: (mode: LocalizationGenerationMode) => void;
}) {
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

    const run = (mode: LocalizationGenerationMode) => {
        setAnchorEl(null);
        onGenerate(mode);
    };

    return (
        <>
            <ButtonGroup variant="outlined" disabled={loading}>
                <Button
                    onClick={() => run('generate')}
                    startIcon={loading ? <CircularProgress size={16} /> : undefined}
                >
                    {loading ? loadingLabel : 'Generate localizations'}
                </Button>
                <Button
                    size="small"
                    onClick={event => setAnchorEl(event.currentTarget)}
                    aria-label="Choose localization generation mode"
                    aria-haspopup="menu"
                >
                    <ArrowDropDownIcon />
                </Button>
            </ButtonGroup>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
            >
                <MenuItem onClick={() => run('generate')}>Generate localizations</MenuItem>
                <MenuItem onClick={() => run('improve')}>Generate and improve localizations</MenuItem>
            </Menu>
        </>
    );
}

function DailyQuestImagePresetPicker({
    kind,
    value,
    presets,
    onSelect,
    onDelete,
}: {
    kind: DailyQuestImageKind;
    value?: string | null;
    presets: DailyQuestImagePresetDto[];
    onSelect: (url: string) => void;
    onDelete: (preset: DailyQuestImagePresetDto) => Promise<void>;
}) {
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const [manage, setManage] = React.useState(false);
    const open = Boolean(anchorEl);

    const close = () => {
        setAnchorEl(null);
        setManage(false);
    };

    return (
        <>
            <Tooltip title={kind === 'COVER' ? 'Pick cover from collection' : 'Pick icon from collection'}>
                <span>
                    <IconButton
                        color={value ? 'primary' : 'default'}
                        onClick={event => setAnchorEl(event.currentTarget)}
                        aria-label="Open image collection"
                    >
                        <CollectionsIcon />
                    </IconButton>
                </span>
            </Tooltip>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Box sx={{ width: 360, maxWidth: '90vw', p: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="subtitle2">Image collection</Typography>
                        <Button size="small" onClick={() => setManage(prev => !prev)}>
                            {manage ? 'Done' : 'Manage'}
                        </Button>
                    </Stack>
                    <Divider sx={{ mb: 1 }} />
                    {presets.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            No images uploaded yet.
                        </Typography>
                    ) : (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                                gap: 1,
                                maxHeight: 320,
                                overflowY: 'auto',
                                pr: 0.5,
                            }}
                        >
                            {presets.map(preset => {
                                const selected = preset.url === value;
                                return (
                                    <Box
                                        key={preset.id}
                                        onClick={() => {
                                            if (manage) return;
                                            onSelect(preset.url);
                                            close();
                                        }}
                                        sx={{
                                            position: 'relative',
                                            height: kind === 'COVER' ? 72 : 88,
                                            borderRadius: 1,
                                            border: '2px solid',
                                            borderColor: selected ? 'primary.main' : 'divider',
                                            overflow: 'hidden',
                                            cursor: manage ? 'default' : 'pointer',
                                            bgcolor: 'background.default',
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={resolveStrapiMediaUrlForDisplay(preset.url) ?? preset.url}
                                            alt=""
                                            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                        />
                                        {manage && (
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={event => {
                                                    event.stopPropagation();
                                                    void onDelete(preset);
                                                }}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 4,
                                                    right: 4,
                                                    bgcolor: 'background.paper',
                                                    '&:hover': { bgcolor: 'background.paper' },
                                                }}
                                                aria-label="Delete image preset"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </Popover>
        </>
    );
}

export default function DailyQuestTemplatePage() {
    const id = useParams()['id'] as string | undefined;
    const isEditMode = id != null;
    const navigate = useNavigate();

    const [form, setForm] = React.useState<UpsertDailyQuestTemplateDto>(emptyTemplateForm);
    const [activationEvents, setActivationEvents] = React.useState<string[]>([]);
    const [userTags, setUserTags] = React.useState<UserTagDefinitionDto[]>([]);
    const [userTaskTemplates, setUserTaskTemplates] = React.useState<UserTaskTemplateDto[]>([]);
    const [imagePresets, setImagePresets] = React.useState<Record<DailyQuestImageKind, DailyQuestImagePresetDto[]>>({
        COVER: [],
        TASK_ICON: [],
    });
    const [loading, setLoading] = React.useState(isEditMode);
    const [saving, setSaving] = React.useState(false);
    const [syncing, setSyncing] = React.useState(false);
    const [templateLocalizationsGenerating, setTemplateLocalizationsGenerating] = React.useState(false);
    const [coverUploading, setCoverUploading] = React.useState(false);
    const [taskUploading, setTaskUploading] = React.useState<Record<number, boolean>>({});
    const [taskLocalizationsGenerating, setTaskLocalizationsGenerating] = React.useState<Record<number, boolean>>({});
    const [expandedTaskIndexes, setExpandedTaskIndexes] = React.useState<Set<number>>(new Set());
    const [saveAttempted, setSaveAttempted] = React.useState(false);
    const [message, setMessage] = React.useState<string | null>(null);
    const [toast, setToast] = React.useState<{ message: string; severity: 'success' | 'error' | 'info' } | null>(null);

    const showToast = (message: string, severity: 'success' | 'error' | 'info' = 'info') => {
        setToast({ message, severity });
    };

    React.useEffect(() => {
        const load = async () => {
            setLoading(true);
            const [funnelOptions, tags, taskTemplates, coverPresets, taskIconPresets, template] = await Promise.all([
                getFunnelOptions(),
                getUserTags(),
                getUserTaskTemplates(),
                getDailyQuestImagePresets('COVER'),
                getDailyQuestImagePresets('TASK_ICON'),
                isEditMode && id ? getDailyQuestTemplate(id) : Promise.resolve(null),
            ]);
            setActivationEvents(funnelOptions.activationEvents ?? []);
            setUserTags((tags ?? []).slice().sort((left, right) => left.tagName.localeCompare(right.tagName)));
            setUserTaskTemplates(taskTemplates ?? []);
            setImagePresets({
                COVER: coverPresets ?? [],
                TASK_ICON: taskIconPresets ?? [],
            });
            if (template) {
                setForm(toForm(template));
            }
            setLoading(false);
        };
        load();
    }, [id, isEditMode]);

    const selectedUserTagOptions = form.requiredTags.map(tagName => (
        userTags.find(tag => tag.tagName === tagName) ?? {
            id: tagName,
            tagName,
            requirements: [],
            approximateUsersCount: null,
            createdAt: '',
        }
    ));

    const handleActivationEventsChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value;
        setForm(prev => ({
            ...prev,
            activationEvents: typeof value === 'string' ? value.split(',') : value,
        }));
    };

    const updateTask = (index: number, patch: Partial<DailyQuestTaskPoolEntryDto>) => {
        setForm(prev => ({
            ...prev,
            taskPool: prev.taskPool.map((task, i) => i === index ? { ...task, ...patch } : task),
        }));
    };

    const toggleTaskExpanded = (index: number) => {
        setExpandedTaskIndexes(prev => {
            const next = new Set(prev);
            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }
            return next;
        });
    };

    const addTask = () => {
        const firstUnused = userTaskTemplates.find(t => !form.taskPool.some(p => p.taskType === t.taskType));
        const fallbackName = userTaskTemplateReferenceName(firstUnused);
        const nextIndex = form.taskPool.length;
        setForm(prev => ({
            ...prev,
            taskPool: [
                ...prev.taskPool,
                {
                    ...blankTask(firstUnused?.taskType ?? 'START_SESSION'),
                    userTaskTemplateId: firstUnused?.id,
                    userTaskTemplateCode: firstUnused?.code,
                    userTaskTemplateImplemented: firstUnused?.implemented ?? false,
                    progressTargets: [firstUnused?.defaultProgressTarget ?? 1],
                    nameLocalizations: fallbackName ? { en: fallbackName } : { en: '' },
                    descriptionLocalizations: fallbackName ? { en: fallbackName } : { en: '' },
                },
            ],
        }));
        setExpandedTaskIndexes(prev => {
            const next = new Set(prev);
            next.add(nextIndex);
            return next;
        });
    };

    const removeTask = (index: number) => {
        setForm(prev => ({ ...prev, taskPool: prev.taskPool.filter((_, i) => i !== index) }));
        setExpandedTaskIndexes(prev => {
            const next = new Set<number>();
            prev.forEach(value => {
                if (value < index) {
                    next.add(value);
                } else if (value > index) {
                    next.add(value - 1);
                }
            });
            return next;
        });
    };

    const generateTemplateLocalizations = async (mode: LocalizationGenerationMode) => {
        const englishTitle = form.titleLocalizations.en?.trim() ?? '';
        const englishDescription = form.descriptionLocalizations.en?.trim() ?? '';
        if (englishTitle.length < 5) {
            showToast('English daily quest title is too short.', 'error');
            return;
        }
        if (englishDescription.length < 5) {
            showToast('English daily quest description is too short.', 'error');
            return;
        }

        setTemplateLocalizationsGenerating(true);
        try {
            const generated = await generateDailyQuestTemplateLocalizations({
                englishTitle,
                englishDescription,
                supportedLanguages: SUPPORTED_LANGUAGES,
                improveEnglish: mode === 'improve',
            });
            if (!generated) {
                throw new Error('Daily quest localizations were not generated.');
            }
            const byId = new Map((generated.localizations ?? []).map(row => [row.id, row.localizations]));
            setForm(prev => ({
                ...prev,
                titleLocalizations: byId.get('title') ?? { en: englishTitle },
                descriptionLocalizations: byId.get('description') ?? { en: englishDescription },
            }));
            showToast(mode === 'improve' ? 'Daily quest localizations generated and improved.' : 'Daily quest localizations generated.', 'success');
        } catch (error) {
            showToast(error instanceof Error ? error.message : 'Daily quest localizations were not generated.', 'error');
        } finally {
            setTemplateLocalizationsGenerating(false);
        }
    };

    const generateTaskLocalizations = async (index: number, mode: LocalizationGenerationMode) => {
        const task = form.taskPool[index];
        if (task.progressTargets.length === 0) {
            setSaveAttempted(true);
            showToast('Add at least one progress target before generating task localizations.', 'error');
            return;
        }
        const taskTemplate = userTaskTemplates.find(template => template.id === task.userTaskTemplateId);
        const taskTypeFallback = task.taskType.toString().replaceAll('_', ' ').toLowerCase();
        const visibleEnglishName = task.nameLocalizations.en?.trim() ?? '';
        const visibleEnglishDescription = task.descriptionLocalizations.en?.trim() ?? '';
        const fallbackName = visibleEnglishName || taskTypeFallback;
        const fallbackDescription = visibleEnglishDescription || fallbackName;

        setTaskLocalizationsGenerating(prev => ({ ...prev, [index]: true }));
        try {
            const generated = await generateDailyQuestLocalizations({
                taskType: task.taskType,
                taskName: userTaskTemplateReferenceName(taskTemplate) || taskTypeFallback,
                currentEnglishName: visibleEnglishName,
                currentEnglishDescription: visibleEnglishDescription,
                progressTargets: task.progressTargets,
                supportedLanguages: SUPPORTED_LANGUAGES,
                improveEnglish: mode === 'improve',
            });
            if (!generated) {
                throw new Error('Task localizations were not generated.');
            }
            const byId = new Map((generated.localizations ?? []).map(row => [row.id, row.localizations]));
            updateTask(index, {
                nameLocalizations: byId.get('name') ?? { en: fallbackName },
                descriptionLocalizations: byId.get('description') ?? { en: fallbackDescription },
            });
            showToast(mode === 'improve' ? 'Task localizations generated and improved.' : 'Task localizations generated.', 'success');
        } catch (error) {
            showToast(error instanceof Error ? error.message : 'Task localizations were not generated.', 'error');
        } finally {
            setTaskLocalizationsGenerating(prev => ({ ...prev, [index]: false }));
        }
    };

    const registerImagePreset = async (kind: DailyQuestImageKind, url: string): Promise<boolean> => {
        const trimmedUrl = url.trim();
        if (!trimmedUrl) return false;
        const preset = await createDailyQuestImagePreset({ kind, url: trimmedUrl });
        if (!preset) return false;
        setImagePresets(prev => ({
            ...prev,
            [kind]: [preset, ...prev[kind].filter(item => item.id !== preset.id && item.url !== preset.url)],
        }));
        return true;
    };

    const registerCurrentImagePreset = async (kind: DailyQuestImageKind, url?: string | null) => {
        const registered = await registerImagePreset(kind, url ?? '');
        setMessage(registered ? 'Image added to collection.' : 'Image URL was not added to collection.');
    };

    const deleteImagePreset = async (kind: DailyQuestImageKind, preset: DailyQuestImagePresetDto) => {
        const deleted = await deleteDailyQuestImagePreset(preset.id);
        if (!deleted) {
            setMessage('Image was not removed from collection.');
            return;
        }
        setImagePresets(prev => ({
            ...prev,
            [kind]: prev[kind].filter(item => item.id !== preset.id),
        }));
    };

    const uploadCoverImage = async (file?: File) => {
        if (!file) return;
        setCoverUploading(true);
        const path = await uploadFile(file, DestinationFolder.Ads);
        setCoverUploading(false);
        if (!path) {
            setMessage('Cover image upload failed.');
            return;
        }
        setForm(prev => ({ ...prev, coverImageUrl: path }));
        await registerImagePreset('COVER', path);
    };

    const uploadTaskIcon = async (index: number, file?: File) => {
        if (!file) return;
        setTaskUploading(prev => ({ ...prev, [index]: true }));
        const path = await uploadFile(file, DestinationFolder.Ads);
        setTaskUploading(prev => ({ ...prev, [index]: false }));
        if (!path) {
            setMessage('Task icon upload failed.');
            return;
        }
        updateTask(index, { iconUrl: path });
        await registerImagePreset('TASK_ICON', path);
    };

    const saveTemplate = async () => {
        setSaveAttempted(true);
        const taskWithoutTargetsIndex = form.taskPool.findIndex(task => task.progressTargets.length === 0);
        if (taskWithoutTargetsIndex >= 0) {
            setExpandedTaskIndexes(prev => {
                const next = new Set(prev);
                next.add(taskWithoutTargetsIndex);
                return next;
            });
            showToast('Each task must have at least one progress target.', 'error');
            return;
        }
        setSaving(true);
        const result = isEditMode && id ? await updateDailyQuestTemplate(id, form) : await createDailyQuestTemplate(form);
        setSaving(false);
        if (!result.ok || !result.data) {
            showToast(result.error ?? 'Template was not saved.', 'error');
            return;
        }
        const saved = result.data;
        showToast('Template saved.', 'success');
        setSaveAttempted(false);
        setForm(toForm(saved));
        if (!isEditMode) {
            navigate('/marketing/daily-quests/templates/' + encodeURIComponent(saved.id) + '/edit', { replace: true });
        }
    };

    const resyncTaskTemplateStatuses = async () => {
        if (!id) return;
        setSyncing(true);
        const result = await resyncDailyQuestTemplateUserTaskTemplates(id);
        setSyncing(false);
        if (!result.ok || !result.data) {
            setMessage(result.error ?? 'Task template statuses were not re-synced.');
            return;
        }
        setForm(toForm(result.data));
        setMessage('Task template statuses re-synced.');
    };

    const selectedTaskTypes = new Set(form.taskPool.map(task => task.taskType));

    return (
        <Box sx={{ mx: 4, my: 3 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={2} sx={{ mb: 2 }}>
                <Typography variant="h4">{isEditMode ? 'Edit daily quest template' : 'New daily quest template'}</Typography>
                <Stack direction="row" gap={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                    {isEditMode && (
                        <Button variant="outlined" onClick={resyncTaskTemplateStatuses} disabled={syncing}>
                            {syncing ? 'Re-syncing...' : 'Re-sync'}
                        </Button>
                    )}
                    <Button variant="outlined" onClick={() => navigate('/marketing/daily-quests')}>Back to daily quests</Button>
                </Stack>
            </Stack>
            {message && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message}</Alert>}
            {loading ? (
                <Typography>Loading...</Typography>
            ) : (
                <Stack gap={2}>
                    <Card variant="outlined">
                        <CardContent>
                            <Stack gap={2}>
                                <TextField label="Reference name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                                <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                                    <TextField
                                        label="Cover image URL"
                                        value={form.coverImageUrl ?? ''}
                                        onChange={e => setForm({ ...form, coverImageUrl: e.target.value || null })}
                                        fullWidth
                                    />
                                    <DailyQuestImagePresetPicker
                                        kind="COVER"
                                        value={form.coverImageUrl}
                                        presets={imagePresets.COVER}
                                        onSelect={url => setForm(prev => ({ ...prev, coverImageUrl: url }))}
                                        onDelete={preset => deleteImagePreset('COVER', preset)}
                                    />
                                    <Tooltip title="Add current URL to collection">
                                        <span>
                                            <IconButton
                                                disabled={!form.coverImageUrl}
                                                onClick={() => void registerCurrentImagePreset('COVER', form.coverImageUrl)}
                                                aria-label="Add cover URL to collection"
                                            >
                                                <AddPhotoAlternateIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Button variant="outlined" component="label" disabled={coverUploading} sx={{ minWidth: 140 }}>
                                        {coverUploading ? <CircularProgress size={20} /> : 'Upload cover'}
                                        <input
                                            hidden
                                            type="file"
                                            accept="image/*"
                                            onChange={e => {
                                                void uploadCoverImage(e.target.files?.[0]);
                                                e.target.value = '';
                                            }}
                                        />
                                    </Button>
                                </Stack>
                                {form.coverImageUrl && (
                                    <Box
                                        component="img"
                                        src={resolveStrapiMediaUrlForDisplay(form.coverImageUrl) ?? form.coverImageUrl}
                                        alt=""
                                        sx={{ width: 180, height: 96, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                                    />
                                )}
                                <FormControlLabel
                                    control={<Switch checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} />}
                                    label={form.active ? 'Enabled' : 'Disabled'}
                                />
                                <FormControl fullWidth>
                                    <InputLabel id="daily-quest-activation-events-label">Activation events</InputLabel>
                                    <Select
                                        labelId="daily-quest-activation-events-label"
                                        multiple
                                        value={form.activationEvents}
                                        onChange={handleActivationEventsChange}
                                        input={<OutlinedInput label="Activation events" />}
                                        renderValue={(selected) => selected.join(', ')}
                                        MenuProps={MenuProps}
                                    >
                                        {activationEvents.map(name => (
                                            <MenuItem key={name} value={name}>
                                                <Checkbox checked={(form.activationEvents?.indexOf(name) ?? -1) > -1} />
                                                <ListItemText primary={name} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Autocomplete
                                    multiple
                                    options={userTags}
                                    value={selectedUserTagOptions}
                                    isOptionEqualToValue={(option, value) => option.tagName === value.tagName}
                                    getOptionLabel={(option) => option.tagName}
                                    onChange={(_, value) => setForm(prev => ({ ...prev, requiredTags: value.map(tag => tag.tagName) }))}
                                    fullWidth
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props}>
                                            <Stack spacing={0.25}>
                                                <Typography variant="body2">{option.tagName}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {option.approximateUsersCount == null
                                                        ? 'Approx. users: counting...'
                                                        : `Approx. users: ${option.approximateUsersCount.toLocaleString()}`}
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    )}
                                    renderInput={(params) => <TextField {...params} label="Required user tags" />}
                                />
                                <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                                    <TextField label="Reward betcoins" type="number" value={form.rewardBetcoins} onChange={e => setForm({ ...form, rewardBetcoins: Number(e.target.value) })} fullWidth />
                                    <TextField label="Tasks to assign" type="number" value={form.tasksToAssignCount} onChange={e => setForm({ ...form, tasksToAssignCount: Number(e.target.value) })} fullWidth />
                                </Stack>
                                <LocalizationEditor
                                    title="Title translations"
                                    textLabel="Title"
                                    value={form.titleLocalizations}
                                    onChange={titleLocalizations => setForm(prev => ({ ...prev, titleLocalizations }))}
                                />
                                <LocalizationEditor
                                    title="Description translations"
                                    textLabel="Description"
                                    value={form.descriptionLocalizations}
                                    onChange={descriptionLocalizations => setForm(prev => ({ ...prev, descriptionLocalizations }))}
                                    minRows={3}
                                />
                                <GenerateLocalizationsButton
                                    loading={templateLocalizationsGenerating}
                                    loadingLabel="Generating daily quest localizations..."
                                    onGenerate={generateTemplateLocalizations}
                                />
                            </Stack>
                        </CardContent>
                    </Card>

                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h6">Task pool</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {form.taskPool.length} tasks in pool / {form.tasksToAssignCount || 0} tasks to assign
                            </Typography>
                        </Box>
                        <Button variant="outlined" onClick={addTask}>Add task</Button>
                    </Stack>
                    {userTaskTemplates.length === 0 && (
                        <Alert severity="warning">
                            User task templates were not loaded. You can still add a row, but template selection will be unavailable until the list is loaded.
                        </Alert>
                    )}
                    {form.taskPool.map((task, index) => {
                        const expanded = expandedTaskIndexes.has(index);
                        const iconUrl = taskIconDisplayUrl(task);
                        return (
                            <Card key={task.id ?? index} variant="outlined">
                                <CardContent>
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        gap={1.5}
                                        alignItems={{ xs: 'stretch', sm: 'center' }}
                                        role="button"
                                        tabIndex={0}
                                        aria-expanded={expanded}
                                        onClick={() => toggleTaskExpanded(index)}
                                        onKeyDown={event => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                toggleTaskExpanded(index);
                                            }
                                        }}
                                        sx={{
                                            cursor: 'pointer',
                                            borderRadius: 1,
                                            '&:focus-visible': {
                                                outline: '2px solid',
                                                outlineColor: 'primary.main',
                                                outlineOffset: 2,
                                            },
                                        }}
                                    >
                                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                                            <Box
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius: 1,
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    bgcolor: 'grey.50',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    overflow: 'hidden',
                                                    flex: '0 0 auto',
                                                }}
                                            >
                                                {iconUrl ? (
                                                    <Box
                                                        component="img"
                                                        src={iconUrl}
                                                        alt=""
                                                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">Icon</Typography>
                                                )}
                                            </Box>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="subtitle1" noWrap>
                                                    {taskPoolEntryDisplayName(task)}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Progress targets: {task.progressTargets.join(' -> ') || '-'}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                        <Tooltip title={expanded ? 'Collapse task' : 'Expand task'}>
                                            <IconButton
                                                onClick={event => {
                                                    event.stopPropagation();
                                                    toggleTaskExpanded(index);
                                                }}
                                                onKeyDown={event => event.stopPropagation()}
                                                aria-label={expanded ? 'Collapse task' : 'Expand task'}
                                                sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}
                                            >
                                                <ArrowDropDownIcon
                                                    sx={{
                                                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                        transition: theme => theme.transitions.create('transform', { duration: theme.transitions.duration.shortest }),
                                                    }}
                                                />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                                        <Divider sx={{ my: 2 }} />
                                        <Stack gap={2}>
                                            <FormControl fullWidth>
                                                <InputLabel>Task template</InputLabel>
                                                <Select
                                                    label="Task template"
                                                    value={task.userTaskTemplateId ?? ''}
                                                    onChange={e => {
                                                        const selected = userTaskTemplates.find(t => t.id === Number(e.target.value));
                                                        if (!selected) return;
                                                        updateTask(index, {
                                                            userTaskTemplateId: selected.id,
                                                            userTaskTemplateCode: selected.code,
                                                            userTaskTemplateImplemented: selected.implemented,
                                                            taskType: selected.taskType,
                                                            progressTargets: [selected.defaultProgressTarget ?? task.progressTargets[0] ?? 1],
                                                            nameLocalizations: {
                                                                ...task.nameLocalizations,
                                                                en: task.nameLocalizations.en || userTaskTemplateReferenceName(selected),
                                                            },
                                                            descriptionLocalizations: {
                                                                ...task.descriptionLocalizations,
                                                                en: task.descriptionLocalizations.en || userTaskTemplateReferenceName(selected),
                                                            },
                                                        });
                                                    }}
                                                >
                                                    {userTaskTemplates.map(t => {
                                                        const isDuplicate = selectedTaskTypes.has(t.taskType) && task.taskType !== t.taskType;
                                                        return (
                                                            <MenuItem key={t.id} value={t.id} disabled={isDuplicate}>
                                                                <Stack direction="row" spacing={1.25} alignItems="center">
                                                                    <TaskImplementationDot implemented={t.implemented} />
                                                                    <ListItemText
                                                                        primary={`${t.code} (${t.taskType})`}
                                                                        secondary={t.name}
                                                                    />
                                                                </Stack>
                                                            </MenuItem>
                                                        );
                                                    })}
                                                </Select>
                                            </FormControl>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <TaskImplementationDot implemented={task.userTaskTemplateImplemented} />
                                                <Typography variant="body2" color="text.secondary">
                                                    {task.userTaskTemplateImplemented ? 'Implemented in v7' : 'Not implemented in v7'}
                                                </Typography>
                                            </Stack>
                                            <TextField label="Task type" value={task.taskType} disabled />
                                            <TextField
                                                label="Progress target"
                                                type="number"
                                                value={task.progressTargets[0] ?? 1}
                                                onChange={e => {
                                                    const value = Number(e.target.value);
                                                    updateTask(index, {
                                                        progressTargets: [Number.isFinite(value) ? value : 1],
                                                    });
                                                }}
                                            />
                                            <FormControl fullWidth>
                                                <InputLabel id={`daily-quest-nav-target-${index}`}>Navigation target (optional)</InputLabel>
                                                <Select
                                                    labelId={`daily-quest-nav-target-${index}`}
                                                    label="Navigation target (optional)"
                                                    value={task.navigationTarget ?? ''}
                                                    onChange={e => {
                                                        const value = e.target.value;
                                                        updateTask(index, {
                                                            navigationTarget: typeof value === 'string' && value.length > 0 ? value : null,
                                                        });
                                                    }}
                                                >
                                                    <MenuItem value="">
                                                        <em>Default (from task type)</em>
                                                    </MenuItem>
                                                    {DAILY_QUEST_NAVIGATION_PRESETS.map(value => (
                                                        <MenuItem key={value} value={value}>{value}</MenuItem>
                                                    ))}
                                                    {DAILY_QUEST_NAVIGATION_MORE.length > 0 && (
                                                        <MenuItem disabled sx={{ opacity: 1, fontSize: '0.75rem', py: 0.5 }}>
                                                            More destinations
                                                        </MenuItem>
                                                    )}
                                                    {DAILY_QUEST_NAVIGATION_MORE.map(value => (
                                                        <MenuItem key={value} value={value}>{value}</MenuItem>
                                                    ))}
                                                </Select>
                                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                                    Overrides where the app navigates when the user taps this task. Leave empty to use the task type default.
                                                </Typography>
                                            </FormControl>
                                            <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                                                <TextField
                                                    label="Task icon URL"
                                                    value={task.iconUrl ?? ''}
                                                    onChange={e => updateTask(index, { iconUrl: e.target.value || null })}
                                                    fullWidth
                                                />
                                                <DailyQuestImagePresetPicker
                                                    kind="TASK_ICON"
                                                    value={task.iconUrl}
                                                    presets={imagePresets.TASK_ICON}
                                                    onSelect={url => updateTask(index, { iconUrl: url })}
                                                    onDelete={preset => deleteImagePreset('TASK_ICON', preset)}
                                                />
                                                <Tooltip title="Add current URL to collection">
                                                    <span>
                                                        <IconButton
                                                            disabled={!task.iconUrl}
                                                            onClick={() => void registerCurrentImagePreset('TASK_ICON', task.iconUrl)}
                                                            aria-label="Add task icon URL to collection"
                                                        >
                                                            <AddPhotoAlternateIcon />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Button variant="outlined" component="label" disabled={taskUploading[index]} sx={{ minWidth: 130 }}>
                                                    {taskUploading[index] ? <CircularProgress size={20} /> : 'Upload icon'}
                                                    <input
                                                        hidden
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={e => {
                                                            void uploadTaskIcon(index, e.target.files?.[0]);
                                                            e.target.value = '';
                                                        }}
                                                    />
                                                </Button>
                                            </Stack>
                                            {task.iconUrl && (
                                                <Box
                                                    component="img"
                                                    src={resolveStrapiMediaUrlForDisplay(task.iconUrl) ?? task.iconUrl}
                                                    alt=""
                                                    sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                                                />
                                            )}
                                            <LocalizationEditor
                                                title="Name translations"
                                                textLabel="Name"
                                                value={task.nameLocalizations}
                                                onChange={nameLocalizations => updateTask(index, { nameLocalizations })}
                                            />
                                            <LocalizationEditor
                                                title="Description translations"
                                                textLabel="Description"
                                                value={task.descriptionLocalizations}
                                                onChange={descriptionLocalizations => updateTask(index, { descriptionLocalizations })}
                                                minRows={2}
                                            />
                                        </Stack>
                                        <CardActions sx={{ px: 0, pb: 0, mt: 1 }}>
                                            <GenerateLocalizationsButton
                                                loading={Boolean(taskLocalizationsGenerating[index])}
                                                loadingLabel="Generating..."
                                                onGenerate={mode => generateTaskLocalizations(index, mode)}
                                            />
                                            <Button color="error" onClick={() => removeTask(index)}>Remove</Button>
                                        </CardActions>
                                    </Collapse>
                                </CardContent>
                            </Card>
                        );
                    })}
                    <Button variant="contained" onClick={saveTemplate} disabled={saving}>{saving ? 'Saving...' : 'Save template'}</Button>
                </Stack>
            )}
            <Snackbar
                open={toast != null}
                autoHideDuration={3500}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setToast(null)}
                    severity={toast?.severity ?? 'info'}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {toast?.message ?? ''}
                </Alert>
            </Snackbar>
        </Box>
    );
}
